import { TZDate, TZDateMini } from '@date-fns/tz';
import { addYears, eachYearOfInterval } from 'date-fns';
import { useMemo, useRef, useState } from 'react';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useCalendarContext } from './useCalendarContext.js';
import { isTimeValue } from '../../../core/time/helpers.js';
import { useCalendarStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import {
  NUMBER_OF_YEARS,
  YEARS_RANGE_START_OFFSET,
} from '../types/calendarConfiguration.js';

export interface SelectYearViewRange {
  rangeStart: TZDate;
  rangeEnd: TZDate;
}

export interface SelectYearViewItems {
  years: TZDate[];
}

/** Custom hook used for the year selection view. */
export function useSelectYear(): SelectYearViewRange & SelectYearViewItems {
  const { state } = useCalendarContext();
  const value = useCalendarStore((store) => store.calendarValue);
  const selectedISODate = isTimeValue(value)
    ? value?.absoluteDate
    : value?.from?.absoluteDate;
  const configuredTimezone = getTimezone();

  const selectedDate = useMemo(
    () =>
      selectedISODate
        ? new TZDateMini(selectedISODate, configuredTimezone)
        : undefined,
    [configuredTimezone, selectedISODate],
  );

  const activeYear = state.activeDate.getFullYear();

  const [rangeStart, setRangeStart] = useState<TZDate>(
    addYears<TZDate, TZDate>(
      selectedDate || state.activeDate,
      YEARS_RANGE_START_OFFSET,
    ),
  );
  const [rangeEnd, setRangeEnd] = useState<TZDate>(() =>
    addYears(rangeStart, NUMBER_OF_YEARS - 1),
  );

  const initialActiveDate = useRef(state.activeDate);

  const years = useMemo(
    () =>
      // don´t need to return TZDateMini as we have the timezone information in the rangeStart.
      eachYearOfInterval({
        start: rangeStart,
        end: rangeEnd,
      }).map((year) => {
        year.setDate(
          selectedDate?.getDate() || initialActiveDate.current.getDate(),
        );
        year.setMonth(
          selectedDate?.getMonth() || initialActiveDate.current.getMonth(),
        );
        return new TZDateMini(year, configuredTimezone);
      }),
    [configuredTimezone, rangeEnd, rangeStart, selectedDate],
  );

  if (activeYear < rangeStart.getFullYear()) {
    initialActiveDate.current = new TZDateMini(
      state.activeDate,
      configuredTimezone,
    );
    // Typecast to TZDate since TypeScript doesn't infer addYears return type directly
    setRangeStart(addYears<TZDate, TZDate>(rangeStart, -NUMBER_OF_YEARS));
    setRangeEnd(addYears<TZDate, TZDate>(rangeEnd, -NUMBER_OF_YEARS));
  }

  if (activeYear > rangeEnd.getFullYear()) {
    initialActiveDate.current = new TZDateMini(
      state.activeDate,
      configuredTimezone,
    );
    // Typecast to TZDate since TypeScript doesn't infer addYears return type directly
    setRangeStart(addYears<TZDate, TZDate>(rangeStart, NUMBER_OF_YEARS));
    setRangeEnd(addYears<TZDate, TZDate>(rangeEnd, NUMBER_OF_YEARS));
  }

  return {
    rangeStart,
    rangeEnd,
    years,
  };
}
